/**
 * 上传队列的状态与文案。
 *
 * 单独拎出来而不是写在页面里，是因为这里全是"算"的部分（百分比、还剩几份、
 * 一句话怎么讲），既不需要 DOM 也不需要网络，可以直接跑测试。页面那边只负责
 * 把 `XMLHttpRequest` 的回调塞进来、把结果画出来。
 */

/**
 * `saving` 是独立的一档，不和 `uploading` 合并：
 * 字节到 100% 之后，服务端还要 HeadObject 核对真实大小与类型，核对通过才落库。
 * 那几百毫秒里进度条是满的但列表还查不到 —— 不给这一档，客户会以为"传到 100%
 * 就没反应了"。
 */
export type UploadStatus = 'queued' | 'uploading' | 'saving' | 'done' | 'failed';

export interface UploadRow {
  /** React key。同名文件在同一批里出现两次也要能区分，所以带上序号 */
  key: string;
  name: string;
  size: number;
  status: UploadStatus;
  /** 已发出的字节。失败时保留最后的值，好让客户看到"传到 30% 断的" */
  sent: number;
  /** 这一份的总字节；`0` 表示长度不可算 */
  total: number;
  error?: string;
}

export function isActive(rows: readonly UploadRow[]): boolean {
  return rows.some(
    (row) => row.status === 'queued' || row.status === 'uploading' || row.status === 'saving',
  );
}

export function rowPercent(row: UploadRow): number {
  if (row.status === 'done') return 100;
  const total = row.total || row.size;
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((row.sent / total) * 100)));
}

/**
 * 整批的百分比。
 *
 * 已完成的一律按 100% 计 —— 只按 `sent` 累加会让失败的条目永远欠着一截，
 * 后面几份传完总条也回不到满格。失败条目的 `sent` 保留原值，因为它确实
 * 消耗了上行带宽。
 */
export function overallPercent(rows: readonly UploadRow[]): number {
  let total = 0;
  let sent = 0;
  for (const row of rows) {
    const size = row.total || row.size;
    total += size;
    sent += row.status === 'done' ? size : Math.min(row.sent, size);
  }
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((sent / total) * 100)));
}

export function rowLabel(row: UploadRow): string {
  switch (row.status) {
    case 'queued':
      return '等待中';
    case 'uploading':
      // 长度不可算时不报百分比：写「0%」比不写更让人怀疑卡住了
      return row.total > 0 ? `上传中 ${rowPercent(row)}%` : '上传中…';
    case 'saving':
      return '服务器核对中';
    case 'done':
      return '已完成';
    case 'failed':
      return '失败';
  }
}
